import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LockKeyhole, LogOut, Trash2, CalendarX, Loader2, CalendarDays, Inbox, MessageSquare, Euro, CreditCard, Banknote, Smartphone, Plus, Pencil, Boxes, Star, MessageCircle, Truck } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import AdminProductDialog from "../components/AdminProductDialog";
import AdminReviewDialog from "../components/AdminReviewDialog";
import { useToast } from "../hooks/use-toast";
import { useSeo } from "../hooks/useSeo";
import { adminLogin, adminGet, adminBlock, adminDeleteBooking, adminUpdateBooking, adminDeleteProduct, getProducts, fileUrl, adminGetReviews, adminDeleteReview, getSettings, adminUpdateSettings, adminUpdateOrder, adminCreateOrder, adminResendOrder, adminDeleteOrder, adminGetDiscounts, adminCreateDiscount, adminUpdateDiscount, adminDeleteDiscount } from "../api";

const TOKEN_KEY = "jnj_admin_token";

const STATUS_META = {
  open: { label: "Open", cls: "bg-amber-100 text-amber-700" },
  aanbetaald: { label: "Aanbetaald", cls: "bg-brand-bluelight text-brand-blue" },
  betaald: { label: "Betaald", cls: "bg-brand-greenlight text-brand-green" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_META[status] || STATUS_META.open;
  return <span className={`inline-flex items-center text-xs font-bold rounded-full px-2.5 py-1 ${s.cls}`} data-testid="reservation-status-badge">{s.label}</span>;
};

const METHOD_META = {
  ideal: { label: "iDEAL", icon: CreditCard, cls: "bg-brand-bluelight text-brand-blue" },
  payment: { label: "iDEAL", icon: CreditCard, cls: "bg-brand-bluelight text-brand-blue" },
  tikkie: { label: "Tikkie", icon: Smartphone, cls: "bg-brand-greenlight text-brand-green" },
  contant: { label: "Contant", icon: Banknote, cls: "bg-amber-100 text-amber-700" },
};

const MethodBadge = ({ method }) => {
  const m = METHOD_META[method] || { label: method || "Onbekend", icon: Euro, cls: "bg-slate-100 text-slate-500" };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1 ${m.cls}`} data-testid="reservation-method-badge">
      <Icon className="w-3.5 h-3.5" /> {m.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-5 flex items-center gap-3 sm:gap-4" data-testid={testId}>
    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center shrink-0 ${accent}`}>
      <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
    </div>
    <div className="min-w-0">
      <p className="text-xl sm:text-2xl font-display font-bold text-brand-navy leading-none">{value}</p>
      <p className="text-xs sm:text-sm text-slate-500 mt-1 truncate">{label}</p>
    </div>
  </div>
);

const fmtNL = (s) => {
  if (!s) return "";
  const p = String(s).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : String(s);
};
const itemDateLabel = (it) => {
  const start = it.start_date || it.date;
  const end = it.end_date || it.start_date || it.date;
  if (start && end && end !== start) return `${fmtNL(start)} t/m ${fmtNL(end)}${it.days ? ` · ${it.days} dagen` : ""}`;
  return fmtNL(start);
};

const waLink = (phone, name, product, date, amount) => {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const intl = digits.startsWith("31") ? digits : digits.startsWith("0") ? "31" + digits.slice(1) : digits;
  const text = `Hoi ${name || ""}! Bedankt voor je reservering van ${product} op ${date}. Te betalen bedrag: €${amount}. Je kunt betalen via deze Tikkie: [plak hier je Tikkie-link]. Groetjes, Jump en Joy`;
  return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
};

const Admin = () => {
  const { toast } = useToast();
  useSeo({ title: "Beheerportaal | Jump en Joy", description: "Beheer reserveringen en beschikbaarheid.", noindex: true });
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);

  const [products, setProducts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewDialog, setReviewDialog] = useState({ open: false, review: null });
  const [googleUrl, setGoogleUrl] = useState("");
  const [reviewsRating, setReviewsRating] = useState("5,0");
  const [reviewsCount, setReviewsCount] = useState("120");
  const [savingSettings, setSavingSettings] = useState(false);
  const [delivery, setDelivery] = useState({ delivery_price_per_km: "", delivery_free_radius_km: "" });
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [newCode, setNewCode] = useState({ code: "", percent: "", max_uses: "" });
  const [savingCode, setSavingCode] = useState(false);
  const [orderEdit, setOrderEdit] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [resendingId, setResendingId] = useState("");
  const [newOrder, setNewOrder] = useState(null);
  const [savingNew, setSavingNew] = useState(false);
  const [block, setBlock] = useState({ product_slug: "", date: "" });
  const [payReq, setPayReq] = useState({ open: false, phone: "", name: "", product: "", date: "", amount: "" });

  const openPayReq = (r) => setPayReq({
    open: true, phone: r.phone || "", name: r.name || "", product: r.product_name || productName(r.product_slug),
    date: r.date || "", amount: String(r.amount || ""),
  });

  const sendPayReq = () => {
    const link = waLink(payReq.phone, payReq.name, payReq.product, payReq.date, payReq.amount || 0);
    if (link) window.open(link, "_blank", "noopener,noreferrer");
    setPayReq((p) => ({ ...p, open: false }));
  };

  const [edit, setEdit] = useState(null); // reservation being edited
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (r) => setEdit({
    id: r.id, product_slug: r.product_slug, date: r.date, name: r.name || "", email: r.email || "",
    phone: r.phone || "", address: r.address || "", amount: String(r.amount || ""), method: r.method || r.source || "ideal",
    pay_status: r.pay_status || "open",
  });

  const editBookedDates = edit
    ? bookings
        .filter((b) => b.product_slug === edit.product_slug && b.id !== edit.id && (b.status === "reserved" || b.status === "blocked"))
        .map((b) => b.date)
    : [];

  const saveEdit = async () => {
    if (!edit) return;
    setSavingEdit(true);
    try {
      await adminUpdateBooking(edit.id, {
        product_slug: edit.product_slug, date: edit.date, name: edit.name, email: edit.email,
        phone: edit.phone, address: edit.address, amount: Number(edit.amount) || 0, method: edit.method,
        pay_status: edit.pay_status,
      }, token);
      await loadAll(token);
      toast({ title: "Reservering bijgewerkt" });
      setEdit(null);
    } catch (e) {
      toast({ title: "Kon niet opslaan", description: e?.response?.data?.detail || "Probeer opnieuw." });
    } finally {
      setSavingEdit(false);
    }
  };

  const loadAll = async (t) => {
    try {
      const [b, r, c, rv, dc] = await Promise.all([
        adminGet("bookings", t), adminGet("reservations", t), adminGet("contacts", t), adminGetReviews(t), adminGetDiscounts(t),
      ]);
      setBookings(b); setReservations(r); setContacts(c); setReviews(rv); setDiscounts(dc);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); }
    }
  };

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
    getSettings().then((s) => {
      setGoogleUrl(s.google_reviews_url || "");
      if (s.reviews_rating) setReviewsRating(s.reviews_rating);
      if (s.reviews_count) setReviewsCount(s.reviews_count);
      setDelivery({
        delivery_price_per_km: s.delivery_price_per_km ?? 0.75,
        delivery_free_radius_km: s.delivery_free_radius_km ?? 25,
      });
    }).catch(() => {});
    if (token) loadAll(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { token: t } = await adminLogin(creds);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      await loadAll(t);
      toast({ title: "Welkom terug!" });
    } catch (e2) {
      toast({ title: "Inloggen mislukt", description: "Controleer je e-mail en wachtwoord." });
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };

  const handleBlock = async (e) => {
    e.preventDefault();
    if (!block.product_slug || !block.date) { toast({ title: "Kies een springkussen en datum" }); return; }
    try {
      await adminBlock(block, token);
      toast({ title: "Datum geblokkeerd", description: "De dag staat nu op bezet." });
      setBlock({ product_slug: "", date: "" });
      loadAll(token);
    } catch (e2) {
      toast({ title: "Kon niet blokkeren", description: e2?.response?.data?.detail || "Probeer opnieuw." });
    }
  };

  const handleDelete = async (id) => {
    try { await adminDeleteBooking(id, token); loadAll(token); toast({ title: "Verwijderd" }); }
    catch (e) { toast({ title: "Kon niet verwijderen" }); }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Deze hele bestelling verwijderen? De gereserveerde data komen weer vrij.")) return;
    try { await adminDeleteOrder(orderId, token); loadAll(token); toast({ title: "Bestelling verwijderd" }); }
    catch (e) { toast({ title: "Kon niet verwijderen" }); }
  };
  const updateOrderStatus = async (orderId, pay_status) => {
    try { await adminUpdateOrder(orderId, { pay_status }, token); loadAll(token); toast({ title: "Status bijgewerkt" }); }
    catch (e) { toast({ title: "Kon status niet bijwerken" }); }
  };

  const openOrderEdit = (o) => {
    setOrderEdit({
      order_id: o.order_id,
      order_no: o.order_no,
      name: o.name || "", email: o.email || "", phone: o.phone || "", address: o.address || "",
      method: o.method || "ideal", pay_status: o.pay_status || "open",
      delivery_cost: o.delivery_cost || 0, discount_amount: o.discount_amount || 0,
      items: o.items.map((it) => ({ booking_id: it.booking_id, product_name: it.product_name, date: it.date, start_date: it.start_date, end_date: it.end_date, days: it.days, amount: it.amount, ondergrond: it.ondergrond || "" })),
    });
  };
  const orderEditTotal = orderEdit
    ? Math.max(0, orderEdit.items.reduce((s, i) => s + (Number(i.amount) || 0), 0) - (Number(orderEdit.discount_amount) || 0)) + (Number(orderEdit.delivery_cost) || 0)
    : 0;
  const saveOrderEdit = async (resendAfter) => {
    if (!orderEdit) return;
    setSavingOrder(true);
    try {
      await adminUpdateOrder(orderEdit.order_id, {
        name: orderEdit.name, email: orderEdit.email, phone: orderEdit.phone, address: orderEdit.address,
        method: orderEdit.method, pay_status: orderEdit.pay_status,
        delivery_cost: Number(orderEdit.delivery_cost) || 0, discount_amount: Number(orderEdit.discount_amount) || 0,
        items: orderEdit.items.map((i) => (i.days > 1 ? { booking_id: i.booking_id } : { booking_id: i.booking_id, date: i.date, amount: Number(i.amount) || 0, ondergrond: i.ondergrond })),
      }, token);
      if (resendAfter) {
        const r = await adminResendOrder(orderEdit.order_id, token);
        toast({ title: "Opgeslagen en mails verstuurd", description: `Naar ${r.sent_to}` });
      } else {
        toast({ title: "Reservering bijgewerkt" });
      }
      setOrderEdit(null);
      loadAll(token);
    } catch (e) { toast({ title: "Kon niet opslaan", description: e?.response?.data?.detail || "Probeer opnieuw." }); }
    finally { setSavingOrder(false); }
  };
  const resendOrder = async (orderId) => {
    setResendingId(orderId);
    try { const r = await adminResendOrder(orderId, token); toast({ title: "Mails opnieuw verstuurd", description: `Bevestiging + aanvraag naar ${r.sent_to}` }); }
    catch (e) { toast({ title: "Kon mails niet versturen", description: e?.response?.data?.detail || "Probeer opnieuw." }); }
    finally { setResendingId(""); }
  };

  const openNewOrder = () => {
    const first = products[0];
    setNewOrder({
      name: "", email: "", phone: "", address: "",
      delivery_cost: 0, discount_amount: 0, method: "contant", pay_status: "open", send_email: false,
      items: [{ product_slug: first?.slug || "", date: "", amount: first?.price ?? "", ondergrond: "" }],
    });
  };
  const setNewItem = (idx, patch) => {
    const items = [...newOrder.items];
    items[idx] = { ...items[idx], ...patch };
    setNewOrder({ ...newOrder, items });
  };
  const onNewProductChange = (idx, slug) => {
    const p = products.find((x) => x.slug === slug);
    setNewItem(idx, { product_slug: slug, amount: p?.price ?? "" });
  };
  const addNewItem = () => setNewOrder({ ...newOrder, items: [...newOrder.items, { product_slug: products[0]?.slug || "", date: "", amount: products[0]?.price ?? "", ondergrond: "" }] });
  const removeNewItem = (idx) => setNewOrder({ ...newOrder, items: newOrder.items.filter((_, i) => i !== idx) });
  const newOrderTotal = newOrder
    ? Math.max(0, newOrder.items.reduce((s, i) => s + (Number(i.amount) || 0), 0) - (Number(newOrder.discount_amount) || 0)) + (Number(newOrder.delivery_cost) || 0)
    : 0;
  const createNewOrder = async () => {
    if (!newOrder.name.trim()) { toast({ title: "Vul de naam van de klant in" }); return; }
    if (newOrder.items.some((i) => !i.product_slug || !i.date)) { toast({ title: "Kies voor elke attractie een product én datum" }); return; }
    setSavingNew(true);
    try {
      const r = await adminCreateOrder({
        items: newOrder.items.map((i) => ({ product_slug: i.product_slug, date: i.date, amount: Number(i.amount) || 0, ondergrond: i.ondergrond })),
        name: newOrder.name.trim(), email: newOrder.email.trim(), phone: newOrder.phone.trim(), address: newOrder.address.trim(),
        delivery_cost: Number(newOrder.delivery_cost) || 0, discount_amount: Number(newOrder.discount_amount) || 0,
        method: newOrder.method, pay_status: newOrder.pay_status, send_email: newOrder.send_email,
      }, token);
      toast({ title: `Reservering ${r.order_no} aangemaakt`, description: newOrder.send_email ? "Bevestigingsmail verstuurd." : undefined });
      setNewOrder(null);
      loadAll(token);
    } catch (e) { toast({ title: "Kon reservering niet aanmaken", description: e?.response?.data?.detail || "Probeer opnieuw." }); }
    finally { setSavingNew(false); }
  };

  const [productDialog, setProductDialog] = useState({ open: false, product: null });
  const reloadProducts = () => getProducts().then(setProducts).catch(() => {});
  const handleDeleteProduct = async (slug) => {
    if (!window.confirm("Dit springkussen verwijderen? Bijbehorende reserveringen vervallen ook.")) return;
    try { await adminDeleteProduct(slug, token); reloadProducts(); loadAll(token); toast({ title: "Springkussen verwijderd" }); }
    catch (e) { toast({ title: "Kon niet verwijderen" }); }
  };

  const productName = (slug) => products.find((p) => p.slug === slug)?.name || slug;

  const reloadReviews = () => token && adminGetReviews(token).then(setReviews).catch(() => {});
  const handleDeleteReview = async (id) => {
    if (!window.confirm("Deze review verwijderen?")) return;
    try { await adminDeleteReview(id, token); reloadReviews(); toast({ title: "Review verwijderd" }); }
    catch (e) { toast({ title: "Kon niet verwijderen" }); }
  };
  const saveSettings = async () => {
    setSavingSettings(true);
    try { await adminUpdateSettings({ google_reviews_url: googleUrl.trim(), reviews_rating: reviewsRating.trim(), reviews_count: reviewsCount.trim() }, token); toast({ title: "Opgeslagen" }); }
    catch (e) { toast({ title: "Kon niet opslaan" }); }
    finally { setSavingSettings(false); }
  };
  const saveDelivery = async () => {
    setSavingDelivery(true);
    try {
      await adminUpdateSettings({
        delivery_price_per_km: Number(delivery.delivery_price_per_km),
        delivery_free_radius_km: Number(delivery.delivery_free_radius_km),
      }, token);
      toast({ title: "Bezorgkosten opgeslagen" });
    } catch (e) { toast({ title: "Kon niet opslaan" }); }
    finally { setSavingDelivery(false); }
  };
  const createCode = async () => {
    const percent = Number(newCode.percent), max_uses = Number(newCode.max_uses);
    if (!newCode.code.trim() || !percent || !max_uses) { toast({ title: "Vul alle velden in", description: "Code, percentage en aantal keer gebruik zijn verplicht." }); return; }
    if (percent < 1 || percent > 100) { toast({ title: "Percentage tussen 1 en 100" }); return; }
    setSavingCode(true);
    try {
      await adminCreateDiscount({ code: newCode.code.trim().toUpperCase(), percent, max_uses, active: true }, token);
      setNewCode({ code: "", percent: "", max_uses: "" });
      loadAll(token);
      toast({ title: "Kortingscode aangemaakt" });
    } catch (e) { toast({ title: "Kon code niet aanmaken", description: e?.response?.data?.detail || "Probeer opnieuw." }); }
    finally { setSavingCode(false); }
  };
  const toggleCode = async (d) => {
    try { await adminUpdateDiscount(d.id, { active: !d.active }, token); loadAll(token); }
    catch (e) { toast({ title: "Kon niet bijwerken" }); }
  };
  const deleteCode = async (id) => {
    if (!window.confirm("Deze kortingscode verwijderen?")) return;
    try { await adminDeleteDiscount(id, token); loadAll(token); toast({ title: "Kortingscode verwijderd" }); }
    catch (e) { toast({ title: "Kon niet verwijderen" }); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-white rounded-3xl shadow-sm border border-slate-100 p-8" data-testid="admin-login-form">
          <div className="w-14 h-14 rounded-2xl bg-brand-bluelight mx-auto flex items-center justify-center">
            <LockKeyhole className="w-7 h-7 text-brand-blue" />
          </div>
          <h1 className="font-display font-bold text-2xl text-brand-navy text-center mt-5">Admin login</h1>
          <p className="text-slate-500 text-sm text-center mt-2">Beheer reserveringen en beschikbaarheid.</p>
          <div className="mt-6 space-y-3">
            <div>
              <Label className="font-semibold text-brand-navy">E-mail</Label>
              <Input data-testid="admin-email-input" className="mt-1.5" type="email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} placeholder="admin@jumpenjoy.nl" />
            </div>
            <div>
              <Label className="font-semibold text-brand-navy">Wachtwoord</Label>
              <Input data-testid="admin-password-input" className="mt-1.5" type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="Wachtwoord" />
            </div>
          </div>
          <button type="submit" data-testid="admin-login-button" disabled={loggingIn} className="w-full mt-6 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white font-bold py-3 transition-colors flex items-center justify-center gap-2 disabled:opacity-70">
            {loggingIn && <Loader2 className="w-4 h-4 animate-spin" />} Inloggen
          </button>
          <Link to="/" className="block text-center text-brand-blue font-semibold text-sm mt-4">Terug naar de website</Link>
        </form>
      </div>
    );
  }

  const reservedCount = bookings.filter((b) => b.status === "reserved").length;
  const blockedCount = bookings.filter((b) => b.status === "blocked").length;
  const revenue = reservations.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="bg-brand-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-display font-bold text-lg sm:text-xl">Jump en Joy · Beheerportaal</h1>
            <p className="text-slate-300 text-xs sm:text-sm">Alle reserveringen, bezette datums en berichten op één plek</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-semibold transition-colors self-start sm:self-auto shrink-0">
            <LogOut className="w-4 h-4" /> Uitloggen
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <StatCard icon={Inbox} label="Reserveringen" value={reservations.length} accent="bg-brand-bluelight text-brand-blue" testId="stat-reservations" />
          <StatCard icon={CalendarDays} label="Bezette dagen" value={reservedCount + blockedCount} accent="bg-brand-pinklight text-brand-pink" testId="stat-booked" />
          <StatCard icon={MessageSquare} label="Berichten" value={contacts.length} accent="bg-amber-100 text-amber-600" testId="stat-contacts" />
          <StatCard icon={Euro} label="Omzet" value={`€${revenue}`} accent="bg-brand-greenlight text-brand-green" testId="stat-revenue" />
        </div>

        <Tabs defaultValue="reservations">
          <TabsList className="mb-6 flex flex-wrap h-auto gap-1.5 w-full sm:w-auto justify-start">
            <TabsTrigger value="reservations" data-testid="tab-reservations">Reserveringen ({reservations.length})</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Springkussens ({products.length})</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews ({reviews.length})</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts">Kortingscodes ({discounts.length})</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">Beschikbaarheid ({bookings.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Berichten ({contacts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="reservations">
            <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-5" data-testid="delivery-settings">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-brand-blue" />
                <Label className="font-semibold text-brand-navy">Bezorgkosten</Label>
              </div>
              <p className="text-slate-500 text-xs mt-1">Binnen de gratis straal rondom Beverwijk is bezorging gratis. Daarbuiten wordt de volledige rijafstand (enkele reis) × prijs per km berekend.</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-3">
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Prijs per km (€)</Label>
                  <Input data-testid="delivery-price-input" type="number" step="0.05" min="0" className="mt-1" value={delivery.delivery_price_per_km} onChange={(e) => setDelivery({ ...delivery, delivery_price_per_km: e.target.value })} />
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Gratis straal (km)</Label>
                  <Input data-testid="delivery-radius-input" type="number" step="1" min="0" className="mt-1" value={delivery.delivery_free_radius_km} onChange={(e) => setDelivery({ ...delivery, delivery_free_radius_km: e.target.value })} />
                </div>
                <button onClick={saveDelivery} disabled={savingDelivery} data-testid="save-delivery-button" className="rounded-full bg-brand-navy hover:bg-brand-navysoft text-white font-bold px-6 py-2.5 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 shrink-0 self-end">
                  {savingDelivery && <Loader2 className="w-4 h-4 animate-spin" />} Opslaan
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-brand-navy text-xl">Reserveringen</h2>
              <button onClick={openNewOrder} data-testid="new-order-button" className="rounded-full bg-brand-pink hover:bg-brand-pinkdark text-white font-bold px-5 py-2.5 transition-colors inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Nieuwe reservering
              </button>
            </div>
            <div className="space-y-3" data-testid="reservations-table">
              {reservations.length === 0 ? (
                <p className="text-slate-500 p-8 text-center bg-white rounded-2xl border border-slate-100">Nog geen reserveringen.</p>
              ) : (
                reservations.map((o) => (
                  <div key={o.order_id} data-testid={`order-${o.order_id}`} className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-brand-navy">{o.order_no || "Reservering"}</span>
                        <MethodBadge method={o.method} />
                        <StatusBadge status={o.pay_status} />
                        {o.discount_code ? <span className="text-xs font-bold text-brand-pink bg-brand-pinklight rounded-full px-2.5 py-1">{o.discount_code} · {o.discount_percent}%</span> : null}
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <select value={o.pay_status} data-testid={`order-status-${o.order_id}`} onChange={(e) => updateOrderStatus(o.order_id, e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-brand-navy">
                          <option value="open">Open</option>
                          <option value="aanbetaald">Aanbetaald</option>
                          <option value="betaald">Betaald</option>
                        </select>
                        {(o.method === "pinnen" || o.method === "contant") && o.phone && (
                          <button onClick={() => openPayReq({ ...o, product_name: (o.items[0]?.product_name || ""), date: o.items[0]?.date, amount: o.total })} data-testid={`order-payment-request-${o.order_id}`} title="Betaalverzoek via WhatsApp" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white transition-colors"><Smartphone className="w-4 h-4" /></button>
                        )}
                        <button onClick={() => resendOrder(o.order_id)} disabled={resendingId === o.order_id} data-testid={`order-resend-${o.order_id}`} title="Bevestiging + aanvraag opnieuw sturen" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-bluelight text-brand-blue hover:bg-brand-blue hover:text-white transition-colors disabled:opacity-50">{resendingId === o.order_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}</button>
                        <button onClick={() => openOrderEdit(o)} data-testid={`order-edit-${o.order_id}`} title="Bewerken" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-brand-navy hover:bg-slate-200 transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteOrder(o.order_id)} data-testid={`order-delete-${o.order_id}`} className="text-brand-pink hover:text-brand-pinkdark"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        {o.items.map((it) => (
                          <div key={it.booking_id} className="flex items-center justify-between text-sm">
                            <span className="text-brand-navy"><span className="font-semibold">{it.product_name}</span> <span className="text-slate-400">· {itemDateLabel(it)}{it.ondergrond ? ` · ${it.ondergrond}` : ""}</span></span>
                            <span className="text-slate-500 whitespace-nowrap">€{it.amount}</span>
                          </div>
                        ))}
                      </div>
                      <div className="text-sm text-slate-500 md:text-right">
                        <div className="text-brand-navy font-semibold">{o.name || "-"}</div>
                        <div>{o.email}</div>
                        <div>{o.phone}</div>
                        <div className="max-w-full break-words">{o.address || "-"}</div>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-end gap-x-5 gap-y-1 text-sm">
                      <span className="text-slate-500">Subtotaal: <span className="font-semibold text-brand-navy">€{o.subtotal}</span></span>
                      {o.discount_amount ? <span className="text-brand-pink">Korting: -€{o.discount_amount}</span> : null}
                      <span className="text-slate-500">Bezorging: <span className="font-semibold text-brand-navy">{o.delivery_cost ? `€${o.delivery_cost}` : "Gratis"}</span></span>
                      <span className="font-display text-brand-navy">Totaal: <span className="text-brand-blue font-bold">€{o.total}</span></span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="products">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-5">
              <p className="text-slate-500 text-sm">Beheer je springkussens. Nieuwe kussens verschijnen direct op de site onder de gekozen categorie.</p>
              <button onClick={() => setProductDialog({ open: true, product: null })} data-testid="add-product-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white font-bold px-5 py-2.5 transition-colors shrink-0">
                <Plus className="w-4 h-4" /> Nieuw springkussen
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
              {products.map((p) => (
                <div key={p.slug} className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid={`product-card-${p.slug}`}>
                  <div className="h-36 bg-slate-100 relative">
                    {p.image && <img src={fileUrl(p.image)} alt={p.name} className="w-full h-full object-cover" />}
                    {p.is_custom && <span className="absolute top-2 left-2 bg-brand-pink text-white text-[10px] font-bold rounded-full px-2 py-0.5">Eigen</span>}
                  </div>
                  <div className="p-4">
                    <p className="font-display text-brand-navy">{p.name}</p>
                    <p className="text-sm text-slate-500">{p.category} · €{p.price}/dag</p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setProductDialog({ open: true, product: p })} data-testid={`edit-product-${p.slug}`} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-slate-200 text-brand-navy text-sm font-bold py-2 hover:bg-slate-50 transition-colors"><Pencil className="w-3.5 h-3.5" /> Bewerken</button>
                      <button onClick={() => handleDeleteProduct(p.slug)} data-testid={`delete-product-${p.slug}`} className="inline-flex items-center justify-center w-9 rounded-full border-2 border-brand-pink/30 text-brand-pink hover:bg-brand-pinklight transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviews">
            <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-5" data-testid="review-settings">
              <Label className="font-semibold text-brand-navy">Google reviews link (voor de knop op de site)</Label>
              <p className="text-slate-500 text-xs mt-1">Plak hier de link naar je Google-reviewpagina. De knop "Bekijk al onze reviews op Google" verschijnt zodra dit is ingevuld.</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-3">
                <Input data-testid="google-reviews-url-input" className="flex-1" value={googleUrl} onChange={(e) => setGoogleUrl(e.target.value)} placeholder="https://g.page/r/...  of  https://maps.app.goo.gl/..." />
                <button onClick={saveSettings} disabled={savingSettings} data-testid="save-google-url-button" className="rounded-full bg-brand-navy hover:bg-brand-navysoft text-white font-bold px-6 py-2.5 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 shrink-0">
                  {savingSettings && <Loader2 className="w-4 h-4 animate-spin" />} Opslaan
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
                <div>
                  <Label className="text-xs text-slate-500">Sterrengemiddelde (knop)</Label>
                  <Input data-testid="reviews-rating-input" className="mt-1" value={reviewsRating} onChange={(e) => setReviewsRating(e.target.value)} placeholder="4,9" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Aantal reviews (knop)</Label>
                  <Input data-testid="reviews-count-input" className="mt-1" value={reviewsCount} onChange={(e) => setReviewsCount(e.target.value)} placeholder="120+" />
                </div>
              </div>
              <p className="text-slate-400 text-[11px] mt-2">Deze waarden tonen in de Google-knop in de hero en bij "Over ons". Klik op Opslaan om alles te bewaren.</p>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-5">
              <p className="text-slate-500 text-sm">Beheer klantreviews. Deze verschijnen in de "Over ons"-sectie op de homepage.</p>
              <button onClick={() => setReviewDialog({ open: true, review: null })} data-testid="add-review-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white font-bold px-5 py-2.5 transition-colors shrink-0">
                <Plus className="w-4 h-4" /> Nieuwe review
              </button>
            </div>

            {reviews.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-500" data-testid="reviews-empty">Nog geen reviews. Voeg je eerste klantreview toe.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reviews-grid">
                {reviews.map((rv) => (
                  <div key={rv.id} className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col" data-testid={`review-card-${rv.id}`}>
                    <div className="flex items-center gap-3">
                      {rv.has_photo && rv.photo ? (
                        <img src={fileUrl(rv.photo)} alt={rv.name} className="w-11 h-11 rounded-full object-cover" />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-brand-bluelight text-brand-blue flex items-center justify-center font-display">{(rv.name || "?").charAt(0).toUpperCase()}</div>
                      )}
                      <div className="min-w-0">
                        <p className="font-display text-brand-navy leading-tight truncate">{rv.name}</p>
                        <div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= rv.rating ? "fill-brand-yellow text-brand-yellow" : "text-slate-200"}`} />)}</div>
                      </div>
                    </div>
                    <p className="text-slate-500 text-sm mt-3 flex-1 leading-relaxed">{rv.text}</p>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => setReviewDialog({ open: true, review: rv })} data-testid={`edit-review-${rv.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-slate-200 text-brand-navy text-sm font-bold py-2 hover:bg-slate-50 transition-colors"><Pencil className="w-3.5 h-3.5" /> Bewerken</button>
                      <button onClick={() => handleDeleteReview(rv.id)} data-testid={`delete-review-${rv.id}`} className="inline-flex items-center justify-center w-9 rounded-full border-2 border-brand-pink/30 text-brand-pink hover:bg-brand-pinklight transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="discounts">
            <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-5" data-testid="discount-create">
              <Label className="font-semibold text-brand-navy">Nieuwe kortingscode</Label>
              <p className="text-slate-500 text-xs mt-1">De korting geldt op de dagprijzen (niet op bezorgkosten). Stel zelf in hoeveel procent en hoe vaak de code gebruikt mag worden.</p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-3">
                <div>
                  <Label className="text-xs text-slate-500">Code</Label>
                  <Input data-testid="discount-code-input" className="mt-1 uppercase" placeholder="ZOMER10" value={newCode.code} onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Percentage (%)</Label>
                  <Input data-testid="discount-percent-input" type="number" min="1" max="100" className="mt-1" placeholder="10" value={newCode.percent} onChange={(e) => setNewCode({ ...newCode, percent: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Aantal keer te gebruiken</Label>
                  <Input data-testid="discount-maxuses-input" type="number" min="1" className="mt-1" placeholder="1" value={newCode.max_uses} onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })} />
                </div>
                <button onClick={createCode} disabled={savingCode} data-testid="discount-create-button" className="rounded-full bg-brand-blue hover:bg-brand-bluedark text-white font-bold px-6 py-2.5 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 self-end">
                  {savingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Aanmaken
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto" data-testid="discounts-table">
              {discounts.length === 0 ? (
                <p className="text-slate-500 p-8 text-center">Nog geen kortingscodes.</p>
              ) : (
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left p-4">Code</th>
                      <th className="text-left p-4">Korting</th>
                      <th className="text-left p-4">Gebruikt</th>
                      <th className="text-left p-4">Status</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {discounts.map((d) => {
                      const spent = d.used_count >= d.max_uses;
                      return (
                        <tr key={d.id} className="border-t border-slate-100" data-testid={`discount-row-${d.code}`}>
                          <td className="p-4 font-display text-brand-navy">{d.code}</td>
                          <td className="p-4 font-semibold">{d.percent}%</td>
                          <td className="p-4 text-slate-500">{d.used_count} / {d.max_uses}</td>
                          <td className="p-4">
                            {!d.active ? <span className="text-xs font-bold rounded-full px-2.5 py-1 bg-slate-100 text-slate-500">Inactief</span>
                              : spent ? <span className="text-xs font-bold rounded-full px-2.5 py-1 bg-brand-pinklight text-brand-pink">Opgebruikt</span>
                              : <span className="text-xs font-bold rounded-full px-2.5 py-1 bg-brand-greenlight text-brand-green">Actief</span>}
                          </td>
                          <td className="p-4 text-right whitespace-nowrap">
                            <button onClick={() => toggleCode(d)} data-testid={`discount-toggle-${d.code}`} className="text-brand-blue hover:text-brand-bluedark font-semibold text-xs mr-4">{d.active ? "Deactiveren" : "Activeren"}</button>
                            <button onClick={() => deleteCode(d.id)} data-testid={`discount-delete-${d.code}`} className="text-brand-pink hover:text-brand-pinkdark align-middle"><Trash2 className="w-4 h-4 inline" /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>


          <TabsContent value="availability">
            <form onSubmit={handleBlock} className="bg-white rounded-2xl border border-slate-100 p-6 flex flex-col sm:flex-row gap-4 sm:items-end mb-6" data-testid="block-date-form">
              <div className="flex-1">
                <Label className="font-semibold text-brand-navy">Springkussen</Label>
                <Select value={block.product_slug} onValueChange={(v) => setBlock({ ...block, product_slug: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="block-product-select"><SelectValue placeholder="Maak een keuze" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="font-semibold text-brand-navy">Datum blokkeren</Label>
                <Input data-testid="block-date-input" className="mt-1.5" type="date" value={block.date} onChange={(e) => setBlock({ ...block, date: e.target.value })} />
              </div>
              <button type="submit" data-testid="block-date-button" className="rounded-full bg-brand-pink hover:bg-brand-pinkdark text-white font-bold px-6 py-2.5 transition-colors inline-flex items-center gap-2">
                <CalendarX className="w-4 h-4" /> Op bezet zetten
              </button>
            </form>

            <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto" data-testid="availability-table">
              {bookings.length === 0 ? (
                <p className="text-slate-500 p-8 text-center">Geen bezette dagen.</p>
              ) : (
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr><th className="text-left p-4">Springkussen</th><th className="text-left p-4">Datum</th><th className="text-left p-4">Type</th><th className="text-left p-4">Klant</th><th className="p-4"></th></tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id} className="border-t border-slate-100" data-testid={`booking-row-${b.id}`}>
                        <td className="p-4 font-semibold text-brand-navy">{productName(b.product_slug)}</td>
                        <td className="p-4 whitespace-nowrap">{b.date}</td>
                        <td className="p-4">
                          <span className={`text-xs font-bold rounded-full px-2.5 py-1 ${b.status === "reserved" ? "bg-brand-greenlight text-brand-green" : "bg-slate-100 text-slate-500"}`}>
                            {b.status === "reserved" ? "Gereserveerd" : "Geblokkeerd"}
                          </span>
                        </td>
                        <td className="p-4 text-slate-500">{b.name || "-"}</td>
                        <td className="p-4 text-right">
                          <button onClick={() => handleDelete(b.id)} data-testid={`booking-delete-${b.id}`} className="text-brand-pink hover:text-brand-pinkdark"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto" data-testid="contacts-table">
              {contacts.length === 0 ? (
                <p className="text-slate-500 p-8 text-center">Nog geen berichten.</p>
              ) : (
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr><th className="text-left p-4">Naam</th><th className="text-left p-4">Contact</th><th className="text-left p-4">Kasteel / datum</th><th className="text-left p-4">Bericht</th></tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr key={c.id} className="border-t border-slate-100 align-top" data-testid={`contact-row-${c.id}`}>
                        <td className="p-4 font-semibold text-brand-navy">{c.name}</td>
                        <td className="p-4 text-slate-500">{c.email}<br />{c.phone}</td>
                        <td className="p-4 text-slate-500">{c.product || "-"}<br />{c.date || ""}</td>
                        <td className="p-4 text-slate-500 max-w-xs">{c.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={payReq.open} onOpenChange={(v) => setPayReq((p) => ({ ...p, open: v }))}>
        <DialogContent className="max-w-md" data-testid="payment-request-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl text-brand-navy">Betaalverzoek versturen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Naar <span className="font-semibold text-brand-navy">{payReq.name || "klant"}</span> ({payReq.phone}) voor <span className="font-semibold text-brand-navy">{payReq.product}</span> op {payReq.date}.</p>
            <div>
              <Label className="font-semibold text-brand-navy">Bedrag (€) dat de klant moet betalen</Label>
              <Input data-testid="payment-request-amount-input" type="number" min="0" step="0.01" className="mt-1.5" value={payReq.amount} onChange={(e) => setPayReq((p) => ({ ...p, amount: e.target.value }))} placeholder="Vul het bedrag in" />
              <p className="text-xs text-slate-400 mt-1.5">Jij bepaalt het bedrag. De klant kan dit niet zelf aanpassen.</p>
            </div>
            <button onClick={sendPayReq} disabled={!payReq.amount} data-testid="payment-request-send-button" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold py-3 transition-colors disabled:opacity-60">
              <Smartphone className="w-4 h-4" /> Open WhatsApp met betaalverzoek
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => { if (!v) setEdit(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-reservation-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl text-brand-navy">Reservering bewerken</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div>
                <Label className="font-semibold text-brand-navy">Springkussen</Label>
                <Select value={edit.product_slug} onValueChange={(v) => setEdit((p) => ({ ...p, product_slug: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="edit-product-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-semibold text-brand-navy">Datum wijzigen</Label>
                <div className="mt-2 rounded-2xl border border-slate-100 p-4">
                  <AvailabilityCalendar bookedDates={editBookedDates} selectable selectedDate={edit.date} onSelect={(d) => setEdit((p) => ({ ...p, date: d }))} />
                </div>
                <p className="text-sm text-brand-green font-semibold mt-2">Gekozen datum: {edit.date}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold text-brand-navy">Naam</Label>
                  <Input data-testid="edit-name-input" className="mt-1.5" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} />
                </div>
                <div>
                  <Label className="font-semibold text-brand-navy">Telefoon</Label>
                  <Input data-testid="edit-phone-input" className="mt-1.5" value={edit.phone} onChange={(e) => setEdit((p) => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="font-semibold text-brand-navy">E-mail</Label>
                <Input data-testid="edit-email-input" className="mt-1.5" value={edit.email} onChange={(e) => setEdit((p) => ({ ...p, email: e.target.value }))} />
              </div>
              <div>
                <Label className="font-semibold text-brand-navy">Adres</Label>
                <Input data-testid="edit-address-input" className="mt-1.5" value={edit.address} onChange={(e) => setEdit((p) => ({ ...p, address: e.target.value }))} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold text-brand-navy">Bedrag (€)</Label>
                  <Input data-testid="edit-amount-input" type="number" min="0" step="0.01" className="mt-1.5" value={edit.amount} onChange={(e) => setEdit((p) => ({ ...p, amount: e.target.value }))} />
                </div>
                <div>
                  <Label className="font-semibold text-brand-navy">Betaalwijze</Label>
                  <Select value={edit.method} onValueChange={(v) => setEdit((p) => ({ ...p, method: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="edit-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ideal">iDEAL</SelectItem>
                      <SelectItem value="tikkie">Tikkie</SelectItem>
                      <SelectItem value="contant">Contant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-semibold text-brand-navy">Betaalstatus</Label>
                <Select value={edit.pay_status} onValueChange={(v) => setEdit((p) => ({ ...p, pay_status: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="aanbetaald">Aanbetaald</SelectItem>
                    <SelectItem value="betaald">Betaald</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setEdit(null)} className="flex-1 rounded-full border-2 border-slate-200 text-brand-navy font-bold py-3 hover:bg-slate-50 transition-colors">Annuleren</button>
                <button onClick={saveEdit} disabled={savingEdit} data-testid="edit-save-button" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white font-bold py-3 transition-colors disabled:opacity-60">
                  {savingEdit && <Loader2 className="w-4 h-4 animate-spin" />} Opslaan
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AdminProductDialog
        open={productDialog.open}
        product={productDialog.product}
        token={token}
        onClose={() => setProductDialog({ open: false, product: null })}
        onSaved={() => { reloadProducts(); }}
      />

      <AdminReviewDialog
        open={reviewDialog.open}
        review={reviewDialog.review}
        token={token}
        onClose={() => setReviewDialog({ open: false, review: null })}
        onSaved={() => { reloadReviews(); }}
      />

      <Dialog open={!!newOrder} onOpenChange={(v) => { if (!v) setNewOrder(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-order-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-brand-navy">Nieuwe reservering aanmaken</DialogTitle></DialogHeader>
          {newOrder && (
            <div className="space-y-5">
              <div>
                <Label className="font-semibold text-brand-navy text-sm">Klantgegevens</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  <Input placeholder="Naam *" data-testid="new-order-name" value={newOrder.name} onChange={(e) => setNewOrder({ ...newOrder, name: e.target.value })} />
                  <Input placeholder="E-mail" data-testid="new-order-email" value={newOrder.email} onChange={(e) => setNewOrder({ ...newOrder, email: e.target.value })} />
                  <Input placeholder="Telefoon" value={newOrder.phone} onChange={(e) => setNewOrder({ ...newOrder, phone: e.target.value })} />
                  <Input placeholder="Adres" value={newOrder.address} onChange={(e) => setNewOrder({ ...newOrder, address: e.target.value })} />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="font-semibold text-brand-navy text-sm">Attracties</Label>
                  <button onClick={addNewItem} data-testid="new-order-add-item" className="text-brand-blue hover:text-brand-bluedark font-semibold text-xs inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Attractie toevoegen</button>
                </div>
                <div className="space-y-2 mt-2">
                  {newOrder.items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`new-order-item-${idx}`}>
                      <select value={it.product_slug} data-testid={`new-order-product-${idx}`} onChange={(e) => onNewProductChange(idx, e.target.value)} className="col-span-4 text-sm border border-slate-200 rounded-lg px-2 py-2 text-brand-navy">
                        {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                      </select>
                      <Input type="date" className="col-span-3" data-testid={`new-order-date-${idx}`} value={it.date} onChange={(e) => setNewItem(idx, { date: e.target.value })} />
                      <div className="col-span-2 relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                        <Input type="number" step="0.01" className="pl-6" data-testid={`new-order-amount-${idx}`} value={it.amount} onChange={(e) => setNewItem(idx, { amount: e.target.value })} />
                      </div>
                      <Input placeholder="Ondergrond" className="col-span-2" value={it.ondergrond} onChange={(e) => setNewItem(idx, { ondergrond: e.target.value })} />
                      {newOrder.items.length > 1 && <button onClick={() => removeNewItem(idx)} className="col-span-1 text-brand-pink hover:text-brand-pinkdark"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Bezorgkosten (€)</Label>
                  <Input type="number" step="0.01" data-testid="new-order-delivery" className="mt-1" value={newOrder.delivery_cost} onChange={(e) => setNewOrder({ ...newOrder, delivery_cost: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Korting (€)</Label>
                  <Input type="number" step="0.01" data-testid="new-order-discount" className="mt-1" value={newOrder.discount_amount} onChange={(e) => setNewOrder({ ...newOrder, discount_amount: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Betaalwijze</Label>
                  <Select value={newOrder.method} onValueChange={(v) => setNewOrder({ ...newOrder, method: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ideal">iDEAL</SelectItem>
                      <SelectItem value="pinnen">Pinnen op locatie</SelectItem>
                      <SelectItem value="contant">Contant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Betaalstatus</Label>
                  <Select value={newOrder.pay_status} onValueChange={(v) => setNewOrder({ ...newOrder, pay_status: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="aanbetaald">Aanbetaald</SelectItem>
                      <SelectItem value="betaald">Betaald</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-brand-navy cursor-pointer">
                <input type="checkbox" data-testid="new-order-send-email" checked={newOrder.send_email} onChange={(e) => setNewOrder({ ...newOrder, send_email: e.target.checked })} className="w-4 h-4 accent-brand-blue" />
                Bevestigingsmail naar de klant sturen (e-mail vereist)
              </label>

              <div className="flex items-center justify-between rounded-xl bg-brand-bluelight px-4 py-3">
                <span className="font-display text-brand-navy">Totaal</span>
                <span className="font-display font-bold text-brand-blue text-lg" data-testid="new-order-total">€{newOrderTotal.toFixed(2).replace(".", ",")}</span>
              </div>

              <button onClick={createNewOrder} disabled={savingNew} data-testid="new-order-create" className="w-full rounded-full bg-brand-pink hover:bg-brand-pinkdark text-white font-bold py-3 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {savingNew ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Reservering aanmaken
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!orderEdit} onOpenChange={(v) => { if (!v) setOrderEdit(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-brand-navy">Reservering bewerken {orderEdit?.order_no}</DialogTitle></DialogHeader>
          {orderEdit && (
            <div className="space-y-5">
              <div>
                <Label className="font-semibold text-brand-navy text-sm">Klantgegevens</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  <Input placeholder="Naam" data-testid="order-edit-name" value={orderEdit.name} onChange={(e) => setOrderEdit({ ...orderEdit, name: e.target.value })} />
                  <Input placeholder="E-mail" data-testid="order-edit-email" value={orderEdit.email} onChange={(e) => setOrderEdit({ ...orderEdit, email: e.target.value })} />
                  <Input placeholder="Telefoon" value={orderEdit.phone} onChange={(e) => setOrderEdit({ ...orderEdit, phone: e.target.value })} />
                  <Input placeholder="Adres" value={orderEdit.address} onChange={(e) => setOrderEdit({ ...orderEdit, address: e.target.value })} />
                </div>
              </div>

              <div>
                <Label className="font-semibold text-brand-navy text-sm">Attracties & prijzen</Label>
                <div className="space-y-2 mt-2">
                  {orderEdit.items.map((it, idx) => (
                    <div key={it.booking_id} className="grid grid-cols-12 gap-2 items-center" data-testid={`order-edit-item-${idx}`}>
                      <span className="col-span-5 text-sm text-brand-navy truncate">{it.product_name}</span>
                      {it.days > 1 ? (
                        <>
                          <span className="col-span-4 text-xs text-slate-500">{itemDateLabel(it)}</span>
                          <span className="col-span-3 text-sm text-brand-navy text-right font-semibold">€{it.amount}</span>
                        </>
                      ) : (
                        <>
                          <Input type="date" className="col-span-3" value={it.date} onChange={(e) => { const items = [...orderEdit.items]; items[idx] = { ...it, date: e.target.value }; setOrderEdit({ ...orderEdit, items }); }} />
                          <div className="col-span-4 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                            <Input type="number" step="0.01" className="pl-7" data-testid={`order-edit-amount-${idx}`} value={it.amount} onChange={(e) => { const items = [...orderEdit.items]; items[idx] = { ...it, amount: e.target.value }; setOrderEdit({ ...orderEdit, items }); }} />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Bezorgkosten (€)</Label>
                  <Input type="number" step="0.01" data-testid="order-edit-delivery" className="mt-1" value={orderEdit.delivery_cost} onChange={(e) => setOrderEdit({ ...orderEdit, delivery_cost: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Korting (€)</Label>
                  <Input type="number" step="0.01" data-testid="order-edit-discount" className="mt-1" value={orderEdit.discount_amount} onChange={(e) => setOrderEdit({ ...orderEdit, discount_amount: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Betaalwijze</Label>
                  <Select value={orderEdit.method} onValueChange={(v) => setOrderEdit({ ...orderEdit, method: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ideal">iDEAL</SelectItem>
                      <SelectItem value="pinnen">Pinnen op locatie</SelectItem>
                      <SelectItem value="contant">Contant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Betaalstatus</Label>
                  <Select value={orderEdit.pay_status} onValueChange={(v) => setOrderEdit({ ...orderEdit, pay_status: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="aanbetaald">Aanbetaald</SelectItem>
                      <SelectItem value="betaald">Betaald</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl bg-brand-bluelight px-4 py-3">
                <span className="font-display text-brand-navy">Nieuw totaal</span>
                <span className="font-display font-bold text-brand-blue text-lg" data-testid="order-edit-total">€{orderEditTotal.toFixed(2).replace(".", ",")}</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => saveOrderEdit(false)} disabled={savingOrder} data-testid="order-edit-save" className="flex-1 rounded-full border-2 border-brand-blue text-brand-blue hover:bg-brand-bluelight font-bold py-3 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {savingOrder && <Loader2 className="w-4 h-4 animate-spin" />} Alleen opslaan
                </button>
                <button onClick={() => saveOrderEdit(true)} disabled={savingOrder} data-testid="order-edit-save-resend" className="flex-1 rounded-full bg-brand-pink hover:bg-brand-pinkdark text-white font-bold py-3 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {savingOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />} Opslaan + mails sturen
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
